import {
  type FriendSex,
  type GetMatchmakerCandidatesParams,
} from '@services/MatchmakerService';

export const FRIEND_AGE_MIN = 18;
export const FRIEND_AGE_MAX = 80;
export const CANDIDATE_AGE_MIN = 18;
export const CANDIDATE_AGE_MAX = 99;

export type MatchmakerFriend = {
  sex: FriendSex;
  age: number;
  city: string;
  ageFrom: number | null;
  ageTo: number | null;
};

export type MatchmakerDeckCriteria = Omit<
  GetMatchmakerCandidatesParams,
  'cursor'
>;

export type CandidateAges = {
  from: number;
  to: number;
};

const clampAge = (age: number) =>
  Math.min(CANDIDATE_AGE_MAX, Math.max(CANDIDATE_AGE_MIN, age));

/**
 * The candidates' ages the server picks for a friend when none are sent: the
 * feed's default age preferences of the friend's sex (server
 * feed/requester.ts hiddenAgeBounds). Shown as a hint only, never sent.
 */
export const defaultCandidateAges = (
  sex: FriendSex,
  age: number,
): CandidateAges => {
  const from = clampAge(sex === '0' ? age - 15 : age - 5);
  const to = clampAge(age + 10);

  return { from: Math.min(from, to), to: Math.max(from, to) };
};

export const isFriendComplete = (
  friend: MatchmakerFriend | null,
): friend is MatchmakerFriend =>
  friend !== null &&
  friend.city !== '' &&
  friend.age >= FRIEND_AGE_MIN &&
  friend.age <= FRIEND_AGE_MAX;

export const deckCriteriaOf = (
  friend: MatchmakerFriend,
): MatchmakerDeckCriteria => ({
  friend_sex: friend.sex,
  friend_age: friend.age,
  city: friend.city,
  ...(friend.ageFrom !== null ? { age_from: friend.ageFrom } : {}),
  ...(friend.ageTo !== null ? { age_to: friend.ageTo } : {}),
});

export const deckCriteriaKey = (criteria: MatchmakerDeckCriteria) =>
  JSON.stringify([
    criteria.friend_sex,
    criteria.friend_age,
    criteria.city,
    criteria.age_from ?? null,
    criteria.age_to ?? null,
  ]);

/** «год», «года» or «лет» after a number of years. */
export const yearsWord = (years: number) => {
  const mod10 = years % 10;
  const mod100 = years % 100;

  if (mod10 === 1 && mod100 !== 11) return 'год';

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'года';

  return 'лет';
};
